import { useClickAway } from '@packages/shared/src/hooks/useClickAway/useClickAway';
import {
  type FocusEventHandler,
  type MouseEventHandler,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

/**
 * A Custom hook for handling navigation popup close behaviors on Hover, Click and Focus
 *
 * @param closeFlyoutAndHamburgerMenu - Function to hide the hamburger menu UI and to close the popover
 * @param allowHoverInteractions - Whether hover interactions are allowed (enables/disables onMouseLeave)
 * @param url - Current URL string (for detecting navigation changes)
 * @param primaryNavigationRef - Primary Navigation Ref Node
 * @param secondaryNavigationRef - Secondary Flyout Item Navigation Ref Node
 */

type NavigationPopoverCloseHandlers = {
  /** Returns an onMouseLeave handler that only closes the PopOver if mouse doesn't enter allowed target */
  handlePrimaryMouseLeave: MouseEventHandler<HTMLDivElement | HTMLUListElement>;
  /** Returns an onMouseLeave handler that only closes the PopOver if mouse doesn't enter allowed target */
  handleSecondaryMouseLeave: MouseEventHandler<HTMLDivElement | HTMLUListElement>;
  /** Returns an onBlur handler that closes only if focus leaves the navigation PopOver */
  onBlurFlyOut: FocusEventHandler<HTMLElement>;
};

type UseNavigationPopoverCloseProps = {
  closeFlyoutAndHamburgerMenu: () => void;
  allowHoverInteractions: boolean;
  url: string;
  primaryNavigationRef: RefObject<HTMLDivElement | HTMLUListElement | null>;
  secondaryNavigationRef: RefObject<HTMLDivElement | HTMLUListElement | null>;
  mainNavigationRef: RefObject<HTMLElement | null>;
};

export const useNavigationPopoverClose = ({
  closeFlyoutAndHamburgerMenu,
  allowHoverInteractions,
  url,
  primaryNavigationRef,
  secondaryNavigationRef,
  mainNavigationRef,
}: UseNavigationPopoverCloseProps): NavigationPopoverCloseHandlers => {
  const lastUrl = useRef(url);
  const lastClickedRef = useRef<HTMLElement | null>(null);

  // this useEffect hook tracks the last clicked node and is used with the "onBlurFlyout" to stop  closing the popover on subsequent clicks due to the firing of Click and Blur Events simultaneously.
  useEffect(() => {
    const handleTrackLastClickedAway = (e: MouseEvent | TouchEvent) => {
      const clickedElement = e.target;
      if (
        clickedElement instanceof HTMLElement &&
        primaryNavigationRef.current?.contains(clickedElement)
      ) {
        const targetlink = clickedElement.closest('a');
        targetlink && (lastClickedRef.current = targetlink);
      }
    };
    document.addEventListener('mousedown', handleTrackLastClickedAway);
    document.addEventListener('touchstart', handleTrackLastClickedAway);
    return () => {
      document.removeEventListener('mousedown', handleTrackLastClickedAway);
      document.removeEventListener('touchstart', handleTrackLastClickedAway);
    };
  });

  /** detects the click outside the main Navigation and closes the Popover. */
  useClickAway(mainNavigationRef, {
    onClickAway: closeFlyoutAndHamburgerMenu,
    strategy: 'start',
  });

  const closeOnUrlChange = useCallback(() => {
    if (lastUrl.current !== url) {
      closeFlyoutAndHamburgerMenu();
    }
    lastUrl.current = url;
  }, [closeFlyoutAndHamburgerMenu, url]);

  /** Closes the popup when the URL changes */
  useEffect(() => {
    closeOnUrlChange();
  }, [closeOnUrlChange, url]);

  const onBlurFlyOut = (e: React.FocusEvent<HTMLDivElement>) => {
    // last clicked element if not the same, will run the closeFlyoutAndHamburgerMenu(), else it won't and allows the popover to close and avoid opening again.
    if (
      lastClickedRef?.current !== e.relatedTarget &&
      !secondaryNavigationRef?.current?.contains(e.relatedTarget)
    ) {
      closeFlyoutAndHamburgerMenu?.();
    }
  };
  const closeOnMouseLeaveUnlessMovingInto = useCallback(
    (allowedTarget: RefObject<HTMLDivElement | HTMLUListElement | null>) =>
      (event: React.MouseEvent<HTMLDivElement | HTMLUListElement>) => {
        if (!allowHoverInteractions) return;

        const mouseIsEnteringAllowedElement =
          event.relatedTarget instanceof Node &&
          allowedTarget.current?.contains(event.relatedTarget);

        if (!mouseIsEnteringAllowedElement) {
          closeFlyoutAndHamburgerMenu();
        }
      },
    [allowHoverInteractions, closeFlyoutAndHamburgerMenu],
  );

  const handlePrimaryMouseLeave = useMemo(
    () => closeOnMouseLeaveUnlessMovingInto(secondaryNavigationRef),
    [closeOnMouseLeaveUnlessMovingInto, secondaryNavigationRef],
  );

  const handleSecondaryMouseLeave = useMemo(
    () => closeOnMouseLeaveUnlessMovingInto(primaryNavigationRef),
    [closeOnMouseLeaveUnlessMovingInto, primaryNavigationRef],
  );

  return {
    handlePrimaryMouseLeave,
    handleSecondaryMouseLeave,
    onBlurFlyOut,
  };
};
